import React, { useState } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Alert from '@amzn/meridian/alert';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Card from '@amzn/meridian/card';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Loader from '@amzn/meridian/loader';
import Checkbox from '@amzn/meridian/checkbox';
import { getDateInUTC } from '../utils/GetDateUtils';
import { SwitchButtonValue, ZoneCreationProperties } from '../types/zoneMapType';

interface ZoneViewerModalsProps {
  openConfirmationModal: boolean;
  setOpenConfirmationModal: (value: boolean) => void;
  publishZoneErrorMsg: string | undefined;
  setPublishZoneErrorMsg: (value: string | undefined) => void;
  publishZoneSuccessful: boolean;
  setPublishZoneSuccessful: (value: boolean) => void;
  onPublishButton: () => Promise<void>;
  store: string;
  timeStamp: number;
  zonesToUpdate: Map<number, ZoneCreationProperties>;
  isLoading: boolean;
  onClosingSuccessfulModal: () => void;
  copyToNewTimestamp: number;
  switchButtonValue: SwitchButtonValue;
  isDraft: boolean;
}

const ZoneViewerModalComponent = (props: ZoneViewerModalsProps) => {
  const {
    openConfirmationModal,
    setOpenConfirmationModal,
    publishZoneErrorMsg,
    setPublishZoneErrorMsg,
    publishZoneSuccessful,
    setPublishZoneSuccessful,
    onPublishButton,
    store,
    timeStamp,
    zonesToUpdate,
    isLoading,
    onClosingSuccessfulModal,
    copyToNewTimestamp,
    switchButtonValue,
    isDraft,
  } = props;

  const [agreementCheckBox, setAgreementCheckBox] = useState<boolean>(false);
  const publishZoneDate = getDateInUTC(
    switchButtonValue === SwitchButtonValue.CopyToNewMap ? copyToNewTimestamp : timeStamp,
  );

  const onCancelPublishButton = () => {
    setAgreementCheckBox(false);
    setOpenConfirmationModal(false);
  };

  return (
    <>
      <Modal open={openConfirmationModal} scrollContainer="viewport" width="780px" aria-describedby="modal-description">
        {isLoading ? (
          <div className="loader-container">
            <Loader type="circular" size="large" />
          </div>
        ) : (
          !publishZoneErrorMsg &&
          !publishZoneSuccessful && (
            <>
              <Row alignmentHorizontal="center" spacingInset="300" spacing="400">
                <Card>
                  <Table headerRows={1} spacing="small">
                    <TableRow>
                      <TableCell alignmentHorizontal="center">StoreId</TableCell>
                      <TableCell alignmentHorizontal="center">Live Date</TableCell>
                      <TableCell alignmentHorizontal="center">Zone Name</TableCell>
                      <TableCell alignmentHorizontal="center">Product Types</TableCell>
                      <TableCell alignmentHorizontal="center">Fixture Ids</TableCell>
                    </TableRow>
                    {Array.from(zonesToUpdate.values()).map((value) => (
                      <TableRow key={value.categoryId}>
                        <TableCell alignmentHorizontal="center">{store}</TableCell>
                        <TableCell alignmentHorizontal="center">
                          <Text>{publishZoneDate}</Text>
                        </TableCell>
                        <TableCell alignmentHorizontal="center">{value.zoneName}</TableCell>
                        <TableCell alignmentHorizontal="center">
                          {value.subcategoryNames?.map((subCategory: string) => (
                            <div>{subCategory}</div>
                          ))}
                        </TableCell>
                        <TableCell alignmentHorizontal="center">
                          {value.fixtureIds?.map((fixtureId: string) => (
                            <div key={fixtureId}>{fixtureId}</div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Table>
                </Card>
              </Row>
              <Alert size="large" type="warning">
                <Checkbox checked={agreementCheckBox} onChange={setAgreementCheckBox}>
                  {isDraft
                    ? `Publishing will create a new draft zone map with live date ${publishZoneDate}. Previous draft zones & selections will be ended and future draft zones will be removed.
                  To complete assortment allocation and promote draft zones to live status, use the 'Bulk Operation' tab on the 'Agile Merchandising' page.`
                    : `Publishing will create a new zone map with live date ${publishZoneDate}. Previous zones & selections will be ended and future zones will be removed.`}
                </Checkbox>
              </Alert>
              <ModalFooter>
                <Row alignmentHorizontal="center" widths="fit">
                  <Button
                    type="secondary"
                    size="large"
                    data-testid="CancelPublishButton"
                    onClick={onCancelPublishButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    data-testid="PublishButton"
                    onClick={onPublishButton}
                    disabled={!agreementCheckBox}
                  >
                    Confirm
                  </Button>
                </Row>
              </ModalFooter>
            </>
          )
        )}
      </Modal>

      {publishZoneErrorMsg && (
        <Modal
          open
          scrollContainer="viewport"
          aria-describedby="modal-description"
          onClose={() => {
            setPublishZoneErrorMsg(undefined);
          }}
        >
          <Alert size="xlarge" type="error">
            <h2>Error Message!</h2>
          </Alert>
          <Text>{publishZoneErrorMsg}</Text>
          <Row alignmentHorizontal="center" widths="fit" />
        </Modal>
      )}

      {publishZoneSuccessful && (
        <Modal
          open={publishZoneSuccessful}
          scrollContainer="viewport"
          aria-describedby="modal-description"
          onClose={() => {
            setPublishZoneErrorMsg(undefined);
            setPublishZoneSuccessful(false);
            onClosingSuccessfulModal();
          }}
        >
          <Alert type="success" size="xlarge">
            Zone Map Published Successfully
          </Alert>
        </Modal>
      )}
    </>
  );
};

export default ZoneViewerModalComponent;
